import { DisaggregatedMetric, MetricPanelProps } from "../../services/types";
import { getMetricGroupChartId, getMetricGroupOrderIds, getSvgMap } from "../../utils/dashboardUtils";
import MetricCard from "../MetricCard";
import IndexBanner from "../IndexBanner";
import DisaggregateMetricCard from "../DisaggregateMetricCard";
import AgeGenderChart from "../charts/AgeGenderChart";
import RaceEthnicityChart from "../charts/RaceEthnicityChart";
import SimpleBarChart from "../charts/SimpleBarChart";

const DemographicMetrics = ({ indicators, metadata, categorizedData, tab, geoDetails, geoTag, selectionActive }: MetricPanelProps) => {   
    const demoOrders = getMetricGroupOrderIds(tab);

    const sortedDemoMetrics = demoOrders.map((order: number[]) => 
        categorizedData.Demographics
            .filter(metric => order.includes(metric.id))
            .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
    console.log('demo data:', sortedDemoMetrics);

    const demoOne: DisaggregatedMetric[] = sortedDemoMetrics[0];

    // Check if city lod Population metric is present, and filter out block group lod
    const hasCityPop = demoOne.some(metric => metric.id === 1184);
    const filteredDemoOne = hasCityPop ? demoOne.filter(metric => metric.id !== 1133) : demoOne;

    const demoTwo: DisaggregatedMetric[] = sortedDemoMetrics[1];

    let metricsForTab = indicators; // Get metrics for the current tab

    // Find the indexMetric from metadata based on the current tab
    const indexMetricId = metadata?.find((metric) => metric.metric_source_type === "index" && metric.metric_category.toLowerCase() === tab.toLowerCase())?.id || 0;

    // Find the full indicator object that matches indexMetric
    const indexMetricData = indicators.find(metric => metric.id === Number(indexMetricId)) || null;

    // Exclude the index metric from the list of metrics
    if (indexMetricId) {
        metricsForTab = metricsForTab.filter((metric) => metric.id !== indexMetricId);
    }

    if (!metricsForTab || metricsForTab.length === 0) {
        return <div className="flex items-center justify-center text-color-cai-dashboard">No metrics available for {tab}.</div>;
    }

    return (
        <>
            <div className="mb-2">
                <IndexBanner
                    key={indexMetricId}
                    metric={indexMetricData}
                    svg={undefined}
                    disaggData={indicators}
                    geoDetails={geoDetails}
                    geoTag={geoTag}
                    selectionActive={selectionActive}
                />
            </div>
            <p className="text-lacey-text mx-4 text-[10px] mb-4">Data updates published April 17th, 2025.</p>
            <p className="text-lacey-text ml-6 text-xs mt-4">Indicators shown below depict demographic details for the population within the selected area.</p>
            <div className="grid grid-cols-2 gap-4 place-items-start ml-4">
                {filteredDemoOne.map(metric => (
                    <MetricCard
                        key={metric.id}
                        metric={metric}
                        width="w-full"
                        svg={getSvgMap(metric.id)}
                        disaggData={indicators}
                        geoDetails={geoDetails}
                        geoTag={geoTag}
                        selectionActive={selectionActive}
                    />
                ))}

                <div className="col-span-2 w-full pr-4">
                    {categorizedData.Demographics.filter(metric => 
                        [getMetricGroupChartId(tab, "race")].includes(metric.id) // Race and Ethnicity
                    ).map(metric => (
                        <DisaggregateMetricCard
                            key={metric.id}
                            id={metric.id}
                            title={"Race & Ethnicity"}
                            svg={getSvgMap(metric.id)}
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                        />
                    ))}
                    <RaceEthnicityChart indicators={indicators} title="Race and Ethnicity" tab="Demographics" chartName="race" height="h-80" barColor="#00A18A" reverseOrder={false} topFive={false}/>
                </div>

                {demoTwo.map(metric => (
                    <MetricCard
                        key={metric.id}
                        metric={metric}
                        width="w-full"
                        svg={getSvgMap(metric.id)}
                        disaggData={indicators}
                        geoDetails={geoDetails}
                        geoTag={geoTag}
                        selectionActive={selectionActive}
                    />
                ))}

                <div className="col-span-2 w-full pr-4 mb-4">
                    {categorizedData.Demographics.filter(metric => 
                        [getMetricGroupChartId(tab, "immigration")].includes(metric.id) // Top Countries of Immigration
                    ).map(metric => (
                        <DisaggregateMetricCard
                            key={metric.id}
                            id={metric.id}
                            title={metric.name}
                            svg={getSvgMap(metric.id)}
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                        />
                    ))}
                    <SimpleBarChart indicators={indicators} title="Top Countries of Immigration" tab={tab} chartName="immigration" height="h-56" barColor="#00A18A" reverseOrder={false} topFive={true}/>
                </div>  

                <div className="col-span-2 w-full pr-4 mb-4">
                    <DisaggregateMetricCard
                        key={1147}
                        id={1147}
                        title={"Age and Sex"}
                        svg={getSvgMap(1147)}
                        disaggData={indicators}
                        geoDetails={geoDetails}
                        geoTag={geoTag}
                        selectionActive={selectionActive}
                        showMetadataIcon={false}
                    />
                    <AgeGenderChart indicators={indicators} title="Age and Sex" height="h-72"/>
                </div>
            </div>
        </>
    );
}

export default DemographicMetrics;